import React, { useState } from 'react';
import axios from 'axios';
import { Mail, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [form, setForm] = useState({ firstname: '', lastname: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.email || !form.firstname) {
      setStatus('error');
      setErrorMsg('Bitte gib mindestens Vorname und E-Mail an.');
      return;
    }
    setStatus('loading');
    try {
      const { data } = await axios.post(`${API}/newsletter`, form);
      if (data.status === 'already_subscribed') {
        setStatus('already');
      } else {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
    }
  };

  return (
    <section className="relative py-24 px-6 lg:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl border border-[#d4b574]/15 bg-gradient-to-br from-[#13130f] via-[#0e0e0c] to-[#0a0a08] p-10 md:p-14">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#d4b574]/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#d4b574]/5 blur-3xl" />

          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#d4b574]/10 border border-[#d4b574]/20 text-[11px] uppercase tracking-[0.22em] text-[#e8d5a8]">
                <Mail size={12} /> Newsletter
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-5 text-[#f5efe3] leading-tight">
                Bleib der Zeit <span className="text-gold-grad italic">voraus</span>
              </h2>
              <p className="mt-4 text-[#9a9285] leading-relaxed">
                Regelmässige kompakte Updates zu aktuellen Marktentwicklungen, Trends und cleveren Strategien.
                <span className="block mt-2 text-[#cfc6b5]">Kein BlaBla. Nur echte Mehrwerte. Direkt in dein Postfach.</span>
              </p>
            </div>

            {status === 'success' || status === 'already' ? (
              <div className="flex flex-col items-start gap-3 p-6 rounded-2xl bg-[#d4b574]/5 border border-[#d4b574]/25">
                <CheckCircle2 className="text-[#e8d5a8]" size={32} />
                <h3 className="font-serif-display text-2xl text-[#e8d5a8]">
                  {status === 'already' ? 'Du bist bereits dabei.' : 'Deine Anmeldung war erfolgreich.'}
                </h3>
                <p className="text-sm text-[#9a9285]">
                  {status === 'already'
                    ? 'Diese E-Mail ist bereits in unserem Verteiler – schön, dass du da bist!'
                    : 'Wir melden uns mit den nächsten Insights bei dir. Schau auch in deinen Spam-Ordner.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder="Vorname"
                    value={form.firstname}
                    onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] placeholder:text-[#7a7060] focus:outline-none focus:border-[#d4b574]/60 focus:bg-black/40 transition"
                  />
                  <input
                    placeholder="Nachname"
                    value={form.lastname}
                    onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] placeholder:text-[#7a7060] focus:outline-none focus:border-[#d4b574]/60 focus:bg-black/40 transition"
                  />
                </div>
                <input
                  type="email"
                  placeholder="E-Mail-Adresse"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] placeholder:text-[#7a7060] focus:outline-none focus:border-[#d4b574]/60 focus:bg-black/40 transition"
                />
                <input
                  placeholder="Telefonnummer (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] placeholder:text-[#7a7060] focus:outline-none focus:border-[#d4b574]/60 focus:bg-black/40 transition"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group w-full mt-2 flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors disabled:opacity-60"
                >
                  {status === 'loading' ? 'Wird gesendet…' : 'Jetzt anmelden und informiert bleiben'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                {status === 'error' && (
                  <p className="text-xs text-red-400">{errorMsg || 'Bitte gib mindestens Vorname und E-Mail an.'}</p>
                )}
                <p className="text-[11px] text-[#7a7060]">Mit der Anmeldung stimmst du der Verarbeitung deiner Daten zu.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
